import { useCallback, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { useCounterPartyController } from 'api/controllers/CounterPartyController';

import { CounterpartyCreateUpdate, CounterpartyType, Invoice } from 'openapi';

import { InvoiceVerificationFieldName } from 'utils/constants/invoiceVerificationFieldNames';

export const useInlineReimbursementCreate = (
  currentInvoice: Invoice,
  onSuccess?: (name: string) => void
) => {
  const { setValue, clearErrors } = useFormContext();
  const { createNewCounterParty } = useCounterPartyController();

  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = useCallback(
    async (name: string) => {
      const trimmedName = name.trim();

      if (!trimmedName) {
        return;
      }

      const createBody: CounterpartyCreateUpdate = {
        name: trimmedName,
        ibans: [],
        type: CounterpartyType.REIMBURSEMENT
      };

      setIsCreating(true);

      try {
        const createdReimbursement = await createNewCounterParty(
          Number(currentInvoice.companyId),
          createBody
        );

        const resolvedReimbursementName =
          createdReimbursement.name || trimmedName;

        setValue(
          InvoiceVerificationFieldName.ReimbursementCounterpartyId,
          createdReimbursement.id ?? null,
          { shouldDirty: true, shouldValidate: true }
        );
        setValue(
          InvoiceVerificationFieldName.ReimbursementCounterpartyName,
          resolvedReimbursementName,
          { shouldDirty: true }
        );
        clearErrors();

        onSuccess?.(resolvedReimbursementName);
      } finally {
        setIsCreating(false);
      }
    },
    [
      clearErrors,
      createNewCounterParty,
      currentInvoice.companyId,
      onSuccess,
      setValue
    ]
  );

  return {
    isCreating,
    handleCreate
  };
};
